/**
 * Generic composer over this package's tracker modules. Lets a consuming
 * app list which modules it wants active in one call, instead of hand-wiring
 * a shared viewSession and repeating shared config across every tracker
 * import itself.
 *
 * Usage (e.g. inside a React effect):
 *
 *   const tracking = createArticleTracking({
 *     containerSelector: ".wp-content",
 *     articleId: article.id,
 *     canonicalUrl: article.canonicalUrl,
 *     transport: "gtm",
 *     measurementId: "G-SNL69JD7W0",
 *     modules: {
 *       scrollDepth: {},
 *       outboundClick: {
 *         internalDomains: ["dailyhive.com"],
 *         excludeLinkSelector: ".article-ad-holder, .viafoura",
 *       },
 *     },
 *   });
 *   tracking.start();
 *
 *   // on client-side navigation to a different article:
 *   tracking.switchArticle({ articleId: next.id, canonicalUrl: next.canonicalUrl });
 *
 *   // on unmount:
 *   tracking.destroy();
 *
 * A module is enabled by including its key under `modules` as an object
 * (even `{}`); omitted, `false`, or `null` disables it. Fields set at the
 * top level (outside `modules`) are shared defaults merged into every
 * enabled module's config; a module's own object can override any of them.
 *
 * viewSession is managed entirely internally -- it is never part of the
 * public config surface here. See the "generatesViewId" note on
 * MODULE_REGISTRY below for why it's only created/injected when at least
 * one enabled module actually originates an article_view_id.
 */

import {
  createArticleScrollDepthTracker,
  type ArticleScrollDepthTrackerConfig,
  type ArticleScrollDepthTracker,
} from "./modules/ArticleScrollDepthTracker";
import {
  createArticleOutboundClickTracker,
  type ArticleOutboundClickTrackerConfig,
  type ArticleOutboundClickTracker,
} from "./modules/ArticleOutboundClickTracker";
import { createArticleViewSession, type ArticleViewSession } from "./shared/createArticleViewSession";
import type { DataLayerTransport } from "./shared/pushDataLayerEvent";

type AnyTracker = ArticleScrollDepthTracker | ArticleOutboundClickTracker;

interface ModuleRegistryEntry {
  factory: (config: Record<string, unknown>) => AnyTracker;
  generatesViewId: boolean;
}

// generatesViewId: true means this module always calls
// viewSession.ensureViewId()/reset() itself (it's a valid *source* of
// article_view_id). false means it only ever reads viewSession.getViewId()
// when handed an external session, and drops its own events if no id has
// been established yet -- so it must never be handed a session unless some
// OTHER enabled module is actually generating one.
const MODULE_REGISTRY: Record<string, ModuleRegistryEntry> = {
  scrollDepth: {
    factory: createArticleScrollDepthTracker as ModuleRegistryEntry["factory"],
    generatesViewId: true,
  },
  outboundClick: {
    factory: createArticleOutboundClickTracker as ModuleRegistryEntry["factory"],
    generatesViewId: false,
  },
};

const SHARED_KEYS = [
  "articleId",
  "canonicalUrl",
  "containerSelector",
  "transport",
  "measurementId",
  "pageType",
  "defaultConsentState",
  "debug",
] as const;

type SharedKey = (typeof SHARED_KEYS)[number];

export interface CreateArticleTrackingConfig {
  articleId?: string | number;
  canonicalUrl?: string;
  containerSelector?: string;
  transport?: DataLayerTransport;
  measurementId?: string;
  pageType?: string;
  defaultConsentState?: "granted" | "denied";
  debug?: boolean;
  modules?: {
    scrollDepth?: Partial<ArticleScrollDepthTrackerConfig> | false | null;
    outboundClick?: Partial<ArticleOutboundClickTrackerConfig> | false | null;
    [key: string]: unknown;
  };
}

export interface ArticleTrackingComposer {
  start(): void;
  switchArticle(next: {
    articleId?: string | number;
    canonicalUrl?: string;
    containerSelector?: string;
  }): void;
  destroy(): void;
  getDebugState(): {
    articleViewId: string | null;
    enabledModules: string[];
    [key: string]: unknown;
  };
}

function warn(cfg: { debug?: boolean }, ...args: unknown[]): void {
  if (cfg.debug && typeof console !== "undefined") {
    console.warn("[createArticleTracking]", ...args);
  }
}

function safely<T>(cfg: { debug?: boolean }, label: string, fn: () => T): T | undefined {
  try {
    return fn();
  } catch (error) {
    warn(cfg, `module "${label}" threw`, error);
    return undefined;
  }
}

function isModuleEnabled(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pick<T extends Record<string, unknown>, K extends string>(
  source: T,
  keys: readonly K[]
): Partial<Record<K, unknown>> {
  const result: Partial<Record<K, unknown>> = {};
  keys.forEach((key) => {
    if (key in source) result[key] = (source as Record<string, unknown>)[key];
  });
  return result;
}

export function createArticleTracking(
  userConfig: CreateArticleTrackingConfig
): ArticleTrackingComposer {
  const raw = userConfig || {};
  const sharedConfig = pick(
    raw as unknown as Record<string, unknown>,
    SHARED_KEYS
  ) as Record<SharedKey, unknown> & { debug?: boolean };
  const modulesConfig =
    typeof raw.modules === "object" && raw.modules !== null ? raw.modules : {};

  Object.keys(raw).forEach((key) => {
    if (key === "modules" || (SHARED_KEYS as readonly string[]).includes(key)) return;
    warn(
      sharedConfig,
      `unknown config key "${key}" -- ignoring. Expected a shared field ` +
        `(${SHARED_KEYS.join(", ")}) or "modules".`
    );
  });

  const registryKeys = Object.keys(MODULE_REGISTRY);
  const enabledKeys: string[] = [];
  Object.keys(modulesConfig).forEach((key) => {
    if (!registryKeys.includes(key)) {
      warn(
        sharedConfig,
        `unknown module "${key}" -- ignoring. Available modules: ` +
          `${registryKeys.join(", ")}.`
      );
      return;
    }
    const moduleValue = (modulesConfig as Record<string, unknown>)[key];
    if (!isModuleEnabled(moduleValue)) {
      if (moduleValue) {
        warn(
          sharedConfig,
          `module "${key}" config must be an object (even {}) to enable it; ` +
            "received a non-object truthy value -- treating as disabled."
        );
      }
      return;
    }
    enabledKeys.push(key);
  });

  if (enabledKeys.length === 0) {
    warn(
      sharedConfig,
      `no modules enabled. Available modules: ${registryKeys.join(", ")}.`
    );
  }

  const hasGenerator = enabledKeys.some(
    (key) => MODULE_REGISTRY[key].generatesViewId
  );
  const viewSession: ArticleViewSession | null = hasGenerator
    ? createArticleViewSession()
    : null;

  const enabledEntries = enabledKeys.map((key) => {
    const overrides = (modulesConfig as Record<string, Record<string, unknown>>)[key];
    if ("viewSession" in overrides) {
      warn(
        sharedConfig,
        `module "${key}" config included "viewSession"; createArticleTracking ` +
          "manages viewSession internally -- ignoring it."
      );
    }
    const merged: Record<string, unknown> = { ...sharedConfig, ...overrides };
    delete merged.viewSession;
    if (viewSession) merged.viewSession = viewSession;

    const instance = MODULE_REGISTRY[key].factory(merged);
    return { key, instance };
  });

  let started = false;
  let destroyed = false;

  function start() {
    if (destroyed || started) return;
    started = true;
    enabledEntries.forEach(({ key, instance }) =>
      safely(sharedConfig, key, () => instance.start())
    );
  }

  function switchArticle(next: {
    articleId?: string | number;
    canonicalUrl?: string;
    containerSelector?: string;
  }) {
    if (destroyed) return;
    const payload = { ...(next || {}) };
    enabledEntries.forEach(({ key, instance }) =>
      safely(sharedConfig, key, () => instance.switchArticle(payload))
    );
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    enabledEntries.forEach(({ key, instance }) =>
      safely(sharedConfig, key, () => instance.destroy())
    );
  }

  function getDebugState() {
    const state: {
      articleViewId: string | null;
      enabledModules: string[];
      [key: string]: unknown;
    } = {
      articleViewId: viewSession ? viewSession.getViewId() : null,
      enabledModules: enabledEntries.map(({ key }) => key),
    };
    enabledEntries.forEach(({ key, instance }) => {
      state[key] = safely(sharedConfig, key, () => instance.getDebugState());
    });
    return state;
  }

  return { start, switchArticle, destroy, getDebugState };
}
